/* ============================================================================
 * useProjectState.ts - Core state management for project data
 * ============================================================================ */

import { useUnconfirmedWrites } from './useUnconfirmedWrites';
import { useState, useRef, useCallback, useEffect, MutableRefObject } from 'react';
import type { ProjectData, ViewFilter } from '@/shared/types/projectData';
import type { WebSocketState, SyncState } from '@/shared/api/WebSocketManager';
import { WebSocketManager } from '@/shared/api/WebSocketManager';
import { SnapshotHistory } from '@/shared/utils/snapshotHistory';
import { 
  loadCompactViewState, 
  saveCompactViewState, 
  loadExpandedNodesState, 
  saveExpandedNodesState 
} from '@/shared/utils/viewStateStorage';

export interface ProjectStateReturn {
  // Core project data state
  projectData: ProjectData | null;
  setProjectData: React.Dispatch<React.SetStateAction<ProjectData | null>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  
  // WebSocket reference
  ws: MutableRefObject<WebSocket | null>;
  
  // Session tracking
  sessionId: MutableRefObject<string>;
  
  // WebSocket state
  wsState: WebSocketState;
  setWsState: React.Dispatch<React.SetStateAction<WebSocketState>>;
  
  // Sync state
  syncState: SyncState;
  setSyncState: React.Dispatch<React.SetStateAction<SyncState>>;
  
  // WebSocket manager reference
  wsManager: MutableRefObject<WebSocketManager | null>;
  
  // HTTP polling timer
  pollTimer: MutableRefObject<NodeJS.Timeout | null>;
  
  // UI State
  expandedNodeIds: Set<string>;
  setExpandedNodeIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectedNodeIds: Set<string>;
  setSelectedNodeIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  filters: ViewFilter;
  setFilters: React.Dispatch<React.SetStateAction<ViewFilter>>;
  searchQuery: string;
  setSearchQueryState: React.Dispatch<React.SetStateAction<string>>;
  sortBy: string | null;
  setSortByState: React.Dispatch<React.SetStateAction<string | null>>;
  isCompactView: boolean;
  setIsCompactViewState: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Undo/redo state
  snapshotHistory: MutableRefObject<SnapshotHistory>;
  undoState: {
    canUndo: boolean;
    canRedo: boolean;
    position: number;
    stackSize: number;
  };
  setUndoState: React.Dispatch<React.SetStateAction<{
    canUndo: boolean;
    canRedo: boolean;
    position: number;
    stackSize: number;
  }>>;
  
  // Unsaved changes
  hasUnsavedChanges: boolean;
  beginUnconfirmedWrite: (key?: string) => () => void;
  getWriteRevision: () => number;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  
  // Undo/redo in progress
  isUndoRedoInProgress: boolean;
  setIsUndoRedoInProgress: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Hook for managing all project state
 */
export function useProjectState(): ProjectStateReturn {
  // Core project data state
  const [projectData, setProjectData] = useState<ProjectData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // WebSocket reference
  const ws = useRef<WebSocket | null>(null);
  
  // Session tracking for undo/redo and synchronization
  const sessionId = useRef(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  // WebSocket connection state
  const [wsState, setWsState] = useState<WebSocketState>('disconnected');
  
  // Synchronization state
  const [syncState, setSyncState] = useState<SyncState>({
    localVersion: 0,
    serverVersion: 0,
    lastSyncTime: 0,
    pendingOperations: [],
    syncStatus: 'synced'
  });
  
  // WebSocket manager reference
  const wsManager = useRef<WebSocketManager | null>(null);
  
  // HTTP polling timer for fallback sync
  const pollTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Tree state - Initialize from localStorage if available
  const [expandedNodeIds, setExpandedNodeIdsInternal] = useState<Set<string>>(() => {
    const stored = loadExpandedNodesState();
    return stored || new Set();
  });
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(new Set());
  
  // Filter and search state
  const [filters, setFilters] = useState<ViewFilter>({});
  const [searchQuery, setSearchQueryState] = useState('');
  const [sortBy, setSortByState] = useState<string | null>(null);
  
  // View preferences - Initialize from localStorage if available
  const [isCompactView, setIsCompactViewInternal] = useState(() => {
    const stored = loadCompactViewState();
    return stored !== null ? stored : false;
  });
  
  // Create wrapped setters that also persist to localStorage
  const setExpandedNodeIds = useCallback<React.Dispatch<React.SetStateAction<Set<string>>>>((value) => {
    setExpandedNodeIdsInternal(prev => {
      const newValue = typeof value === 'function' ? value(prev) : value;
      // Save to localStorage
      saveExpandedNodesState(newValue);
      return newValue;
    });
  }, []);
  
  const setIsCompactViewState = useCallback<React.Dispatch<React.SetStateAction<boolean>>>((value) => {
    setIsCompactViewInternal(prev => {
      const newValue = typeof value === 'function' ? value(prev) : value;
      // Save to localStorage
      saveCompactViewState(newValue);
      return newValue;
    });
  }, []);
  
  // Snapshot history retained for diagnostics/performance tooling
  const snapshotHistory = useRef(new SnapshotHistory({
    maxSnapshots: 100,
    enableCompression: true,
    enablePerformanceMonitoring: true,
    backgroundProcessing: true
  }));
  useEffect(() => {
    const history = snapshotHistory.current;
    return () => history.clear();
  }, []);
  
  // Undo/redo state - synchronized from the authoritative operation history
  const [undoState, setUndoState] = useState({
    canUndo: false,
    canRedo: false,
    position: -1,
    stackSize: 0
  });
  
  // Unsaved changes tracking
  const [hasLocalChanges, setHasUnsavedChanges] = useState(false);
  const { hasUnconfirmedWrites, beginUnconfirmedWrite, getWriteRevision } = useUnconfirmedWrites();
  const hasUnsavedChanges = hasLocalChanges || hasUnconfirmedWrites;
  
  // Track if we're currently processing an undo/redo to prevent auto-initialization interference
  const [isUndoRedoInProgress, setIsUndoRedoInProgress] = useState(false);
  
  return {
    projectData,
    setProjectData,
    error,
    setError,
    isLoading,
    setIsLoading,
    ws,
    sessionId,
    wsState,
    setWsState,
    syncState,
    setSyncState,
    wsManager,
    pollTimer,
    expandedNodeIds,
    setExpandedNodeIds,
    selectedNodeIds,
    setSelectedNodeIds,
    filters,
    setFilters,
    searchQuery,
    setSearchQueryState,
    sortBy,
    setSortByState,
    isCompactView,
    setIsCompactViewState,
    snapshotHistory,
    undoState,
    setUndoState,
    hasUnsavedChanges,
    beginUnconfirmedWrite,
    getWriteRevision,
    setHasUnsavedChanges,
    isUndoRedoInProgress,
    setIsUndoRedoInProgress
  };
}
